import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from "react";
import { cn } from "./cn";
import { controlClasses } from "./Field";

export interface DateTimeRangeValue {
  start: string;
  end: string;
}

export interface DateTimeRangeLabels {
  placeholder: string;
  startTime: string;
  endTime: string;
  done: string;
  previousMonth: string;
  nextMonth: string;
  invalidRange: string;
}

export interface DateTimeRangeProps {
  value: DateTimeRangeValue;
  onChange: (value: DateTimeRangeValue) => void;
  labels: DateTimeRangeLabels;
  locale?: string;
  error?: ReactNode;
  disabled?: boolean;
  defaultStartTime?: string;
  defaultEndTime?: string;
  className?: string;
}

const pad = (n: number) => String(n).padStart(2, "0");

const splitValue = (value: string) => {
  const [day = "", time = ""] = value.split("T");
  return { day, time };
};

const joinValue = (day: string, time: string) => `${day}T${time}`;

const dayKey = (date: Date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

const parseDay = (key: string) => {
  const [year, month, day] = key.split("-").map(Number);
  return year && month && day ? new Date(year, month - 1, day) : null;
};

const monthCells = (view: Date) => {
  const first = new Date(view.getFullYear(), view.getMonth(), 1);
  const lead = (first.getDay() + 6) % 7;
  const total = new Date(view.getFullYear(), view.getMonth() + 1, 0).getDate();
  return [
    ...Array.from({ length: lead }, () => null),
    ...Array.from({ length: total }, (_, i) => new Date(view.getFullYear(), view.getMonth(), i + 1)),
  ];
};

const MOMENT_FORMAT: Intl.DateTimeFormatOptions = {
  weekday: "short",
  day: "numeric",
  month: "short",
  hour: "2-digit",
  minute: "2-digit",
};

const formatMoment = (value: string, locale: string) => {
  const date = value ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime()) ? date.toLocaleString(locale, MOMENT_FORMAT) : "";
};

export function DateTimeRange({
  value,
  onChange,
  labels,
  locale = "es",
  error,
  disabled,
  defaultStartTime = "00:00",
  defaultEndTime = "23:59",
  className,
}: DateTimeRangeProps) {
  const panelId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [pickingEnd, setPickingEnd] = useState(false);

  const start = splitValue(value.start);
  const end = splitValue(value.end);
  const [viewMonth, setViewMonth] = useState(() => parseDay(start.day) ?? new Date());

  const invalidRange = Boolean(value.start && value.end && value.end <= value.start);
  const message = error ?? (invalidRange ? labels.invalidRange : null);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event: MouseEvent) => {
      rootRef.current && !rootRef.current.contains(event.target as Node) && setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => event.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const weekdays = useMemo(
    () => Array.from({ length: 7 }, (_, i) => new Date(2024, 0, 1 + i).toLocaleDateString(locale, { weekday: "narrow" })),
    [locale]
  );
  const cells = useMemo(() => monthCells(viewMonth), [viewMonth]);
  const monthLabel = viewMonth.toLocaleDateString(locale, { month: "long", year: "numeric" });

  const startTime = start.time || defaultStartTime;
  const endTime = end.time || defaultEndTime;

  const pickDay = (key: string) => {
    if (!start.day || !pickingEnd || key < start.day) {
      onChange({ start: joinValue(key, startTime), end: joinValue(key, endTime) });
      setPickingEnd(true);
      return;
    }
    onChange({ start: value.start, end: joinValue(key, endTime) });
    setPickingEnd(false);
  };

  const shiftMonth = (delta: number) =>
    setViewMonth((month) => new Date(month.getFullYear(), month.getMonth() + delta, 1));

  const startLabel = formatMoment(value.start, locale);
  const endLabel = formatMoment(value.end, locale);

  return (
    <div ref={rootRef} className={cn("relative w-full", className)}>
      <button
        type="button"
        disabled={disabled}
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((current) => !current)}
        className={cn(
          controlClasses(Boolean(message), "md"),
          "flex items-center gap-2 text-left",
          open ? "border-primary-500 ring-2 ring-primary-500/30" : undefined
        )}
      >
        <i className="ri-calendar-line shrink-0 text-content-faint" aria-hidden />
        {startLabel ? (
          <span className="min-w-0 flex-1 truncate text-content">
            {startLabel}
            {endLabel ? ` → ${endLabel}` : ""}
          </span>
        ) : (
          <span className="min-w-0 flex-1 truncate text-content-faint">{labels.placeholder}</span>
        )}
        <i
          className={cn("ri-arrow-down-s-line shrink-0 text-content-faint transition-transform", open ? "rotate-180" : undefined)}
          aria-hidden
        />
      </button>

      {message ? <span className="mt-1 block text-xs text-error">{message}</span> : null}

      {open && (
        <div
          id={panelId}
          role="dialog"
          className="absolute left-0 right-0 z-30 mt-1 rounded-lg border border-line bg-card p-3 shadow-dropdown"
        >
          <div className="mb-2 flex items-center justify-between">
            <button
              type="button"
              onClick={() => shiftMonth(-1)}
              title={labels.previousMonth}
              aria-label={labels.previousMonth}
              className="p-1 text-content hover:text-primary-500"
            >
              <i className="ri-arrow-left-s-line" />
            </button>
            <span className="text-sm font-medium capitalize text-content">{monthLabel}</span>
            <button
              type="button"
              onClick={() => shiftMonth(1)}
              title={labels.nextMonth}
              aria-label={labels.nextMonth}
              className="p-1 text-content hover:text-primary-500"
            >
              <i className="ri-arrow-right-s-line" />
            </button>
          </div>

          <div className="mb-1 grid grid-cols-7 gap-1 text-center">
            {weekdays.map((weekday, i) => (
              <span key={i} className="text-[11px] uppercase text-content-faint">
                {weekday}
              </span>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {cells.map((day, i) => {
              if (!day) return <span key={i} />;
              const key = dayKey(day);
              const isEdge = key === start.day || key === end.day;
              const inRange = Boolean(start.day && end.day && key > start.day && key < end.day);
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => pickDay(key)}
                  className={cn(
                    "flex h-8 items-center justify-center rounded-full text-xs",
                    isEdge
                      ? "bg-primary-500 text-on-primary"
                      : inRange
                      ? "bg-primary-500/15 text-content"
                      : "text-content hover:bg-subtle"
                  )}
                >
                  {day.getDate()}
                </button>
              );
            })}
          </div>

          <div className="mt-3 grid grid-cols-2 gap-2">
            <label className="flex flex-col gap-1 text-[11px] text-content-faint">
              {labels.startTime}
              <input
                type="time"
                disabled={!start.day}
                value={start.day ? startTime : ""}
                onChange={(event) => onChange({ start: joinValue(start.day, event.target.value), end: value.end })}
                className={controlClasses(false, "sm")}
              />
            </label>
            <label className="flex flex-col gap-1 text-[11px] text-content-faint">
              {labels.endTime}
              <input
                type="time"
                disabled={!end.day}
                value={end.day ? endTime : ""}
                onChange={(event) => onChange({ start: value.start, end: joinValue(end.day, event.target.value) })}
                className={controlClasses(false, "sm")}
              />
            </label>
          </div>

          <button
            type="button"
            onClick={() => setOpen(false)}
            className="mt-3 w-full rounded-lg bg-primary-500 px-2 py-1.5 text-xs font-medium text-on-primary hover:bg-primary-400"
          >
            {labels.done}
          </button>
        </div>
      )}
    </div>
  );
}
